import {UntypedFormArray, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {EditPermissions} from "../../../../../../../shared/models";
import {newStepValue} from "../../../../../services/flowBuilder/utils/constants";
import {required} from "../../../../../../../shared/utils";
import {getEditTypes} from "../../../delegatorUtils";


const hashAlgorithms = {
    MD2: 'MD2',
    MD5: 'MD5',
    'SHA-1': 'SHA-1',
    'SHA-256': 'SHA-256',
    'SHA-384': 'SHA-384',
    'SHA-512': 'SHA-512',
}

const encodingAlgorithms = {
    base64: 'Base64',
    hex: 'Hex',
}

const encryptAlgorithms = {openpgp: 'Openpgp'}

export const algorithmGroup = {
    encode: encodingAlgorithms,
    encrypt: encryptAlgorithms,
    hash: hashAlgorithms,
}

export enum EncryptTypes {
    HASH = 'hash',
    ENCRYPT = 'encrypt',
    ENCODE = 'encode'
}

export interface EncryptData {
    name: string,
    description: string,
    algorithm: string,
    yes: string,
    no: string,
    strings: string[] | Strings,
}

type Strings = [{ value: string }]

export interface AlgorithmsData {
    method: EncryptTypes,
    encoding: string,
    compression: string,
    publicKey: string,
}

export interface EncryptForm {
    encryptFrom: UntypedFormGroup,
    algorithmsForm: UntypedFormGroup
}

export class Encrypt {

    action = {}

    constructor() {
        this.action = {
            'actionType': 'encrypt',
        };
    }

    static toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): EncryptForm {
        const {editMenuTextOnly, editFlows} = getEditTypes(editPermissions);
        const encryptData = this.getEncryptData(stepData);
        const algorithmsData = this.getAlgorithmsData(stepData);

        return {
            encryptFrom: this.buildEncryptFrom(encryptData, editFlows),
            algorithmsForm: this.buildAlgorithmsForm(algorithmsData, editFlows)
        }
    }

    private static getEncryptData(stepData: { [index: string]: any }): EncryptData {
        const action = stepData?.action ? stepData?.action : {};
        const actionParams = action?.actionParams ? action?.actionParams : {};
        const result = action?.result ? action?.result : {};

        return {
            name: action.name ? action.name : '',
            description: action.description ? action.description : '',
            algorithm: actionParams.algorithm ? actionParams.algorithm : '',
            yes: result.yes ? result.yes : newStepValue,
            no: result.no ? result.no : newStepValue,
            strings: actionParams.strings ? actionParams.strings : [''],
        };
    }

    private static buildEncryptFrom(formData: EncryptData, editFlows: boolean) {
        return new UntypedFormGroup({
            name: new UntypedFormControl({
                value: formData.name,
                disabled: !editFlows
            }, required('Name')),
            description: new UntypedFormControl({
                value: formData.description,
                disabled: !editFlows
            }, required('Description')),
            strings: this.buildStringsArray(formData.strings as string[], editFlows),
            algorithm: new UntypedFormControl({
                value: formData.algorithm,
                disabled: !editFlows
            }, required('Algorithm')),
            yes: new UntypedFormControl(formData.yes),
            no: new UntypedFormControl(formData.no),
        });
    }

    private static getAlgorithmsData(stepData: { [index: string]: any }): AlgorithmsData {
        const action = stepData?.action ? stepData?.action : {};
        const actionParams = action?.actionParams ? action?.actionParams : {};

        return {
            encoding: actionParams.encoding ? actionParams.encoding : '',
            method: actionParams.method ? actionParams.method : '',

            compression: actionParams.compression ? actionParams.compression : '',
            publicKey: actionParams.publicKey ? actionParams.publicKey : '',
        };
    }

    static buildAlgorithmsForm(formData: AlgorithmsData, editFlows: boolean): UntypedFormGroup {
        switch (formData.method) {
            case EncryptTypes.ENCODE:
            case EncryptTypes.HASH:
                return this.buildEncodeAndHashForm(formData, editFlows)
            case EncryptTypes.ENCRYPT:
                return this.buildEncryptForm(formData, editFlows);
            default:
                return new UntypedFormGroup({});
        }
    }

    private static buildEncodeAndHashForm(formData: AlgorithmsData, editFlows: boolean): UntypedFormGroup {
        return new UntypedFormGroup({
            encoding: new UntypedFormControl({
                value: formData.encoding,
                disabled: !editFlows
            }, required('Encoding')),
            method: new UntypedFormControl({
                value: formData.method,
                disabled: !editFlows
            }, required('Method')),
        });
    }

    private static buildEncryptForm(formData: AlgorithmsData, editFlows: boolean): UntypedFormGroup {
        return new UntypedFormGroup({
            compression: new UntypedFormControl({
                value: formData.compression,
                disabled: !editFlows
            }, required('Compression')),
            encoding: new UntypedFormControl({
                value: formData.encoding,
                disabled: !editFlows
            }, required('Encoding')),
            method: new UntypedFormControl({
                value: formData.method,
                disabled: !editFlows
            }, required('Method')),
            publicKey: new UntypedFormControl({
                value: formData.publicKey,
                disabled: !editFlows
            }, required('Method')),
        });
    }

    private static buildStringsArray(data: string[], editFlows: boolean): UntypedFormArray {
        const strings = new UntypedFormArray([]);
        for (let value of data) {
            strings.push(this.stringsTemplate(value, editFlows));
        }
        return strings;
    }

    public static stringsTemplate(value: string, editFlows: boolean): UntypedFormGroup {
        return new UntypedFormGroup({
            value: new UntypedFormControl({
                value,
                disabled: !editFlows
            })
        })
    }

    public addActionData(encryptData: EncryptData, algorithmsData: AlgorithmsData) {

        const {name, description, algorithm, strings, yes, no} = encryptData;

        let actionParams: {} = {
            algorithm,
            strings: this.toStringsArray(strings as Strings)
        }

        if (algorithmsData) {
            const {method, encoding} = algorithmsData

            actionParams = {
                ...actionParams,
                method,
                encoding
            }

            if (method === EncryptTypes.ENCRYPT) {
                const {publicKey, compression} = algorithmsData

                actionParams = {
                    ...actionParams,
                    publicKey,
                    compression
                }
            }
        }

        const result = {yes, no};

        this.action = {
            ...this.action,
            name,
            description,
            actionParams,
            result
        }

    }

    public get nodeData(): {} {
        return {
            action: this.action,
            'type': 'action',
            metadata: {
                nodeVersion: '1.0.0'
            }
        };
    }

    private toStringsArray(value: Strings): string[] {
        const strings = [];
        for (let valueElement of value) {
            strings.push(valueElement.value)
        }
        return strings;
    }
}

// {
//     action: {
//        name: '',
//        description: '',
//        actionType: 'encrypt',
//        actionParams: {
//          strings: [''],
//          algorithm: formBuilderConstants.DEFAULT_SELECT_OPTION_VALUE,
//        },
//        result: {
//          yes: newStepValue,
//          no: newStepValue,
//        },
//     },
//     type: 'action',
// }
